/**
 * Meet the team — what each model is for, and where each one stops.
 *
 * Same folder rule as landing.ts and about.ts: user-facing strings live here so
 * content.test.ts can scan one folder. It matches DOUBLE-quoted literals only.
 *
 * WHY THIS PAGE EXISTS. The landing page's Personas band gives Kev and Franco a
 * card each, which is enough to tell them apart and not enough to tell you what
 * to do with either. This page states the intended purpose of both, in the terms
 * a reasonable user would need to understand what they are buying.
 *
 * THE FAIR PRICE, AND THE CAVEAT ON IT. Kev converts its own probability to a
 * break-even decimal price (1/p). That is shipped — nba/lambda_nba_chat.py
 * computes fairPriceOver/Under and its own `fairPriceMeans` string is the
 * wording this page follows:
 *
 *     "break-even decimal odds implied by KEV's own probability (1/p) — NOT a
 *      market price and NOT a claim about anyone's odds ... Whether the price on
 *      offer beats it is the punter's to check; KEV cannot see it."
 *
 * NO ODDS FEED IS CONNECTED. So the caveat in KEV_MODEL.body is load-bearing and
 * must not be softened or dropped: Kev cannot see a bookmaker's price, cannot
 * say whether one is good, and never names a bet. Anything that implies a
 * model-vs-market comparison re-introduces the framing §7 strips out.
 *
 * HOW THE NOT-A-TIPPING-SERVICE ARGUMENT IS BUILT. Structurally, not defensively
 * — §2.3 bans a banner that advertises the limitation. The argument is simply
 * the true description: Kev produces a probability and the price it implies, it
 * cannot see what you have been offered, and the comparison is the reader's
 * step. Franco naming a starter is the control that shows the line is drawn on
 * purpose rather than by accident, because a lineup choice stakes nothing.
 * Nowhere does this page say "we are not a tipping service" — it gives the
 * reader the facts that make the conclusion theirs.
 *
 * ACRONYMS. "Knowledge Engine for Value" is the brand's own, from the marketing
 * plan's title line. FRANCO is new (2026-08-12): Fantasy Roster ANalysis
 * COmpanion, with CO for Companion at Ned's request.
 *
 * NOT HERE, DELIBERATELY:
 *   §1.3  no MAE, hit rate, win rate or any accuracy figure — the model docs
 *         carry them and they are a performance claim without the ledger
 *   §2.3  no stake sizing, and no announcing that we don't do it
 *   §3    the RG line is the FOOTER's job, on every page. Repeating it in body
 *         copy is the banner instinct §2.3 warns about
 *   no sport is named; COVERAGE points at /pricing, which reads the catalogue
 */

export const TEAM_INTRO = {
  eyebrow: "Meet The Team",
  heading: "Two models, two jobs",
  body: [
    "Kev does sport. Franco does fantasy. They share the data and the rules.",
    /* "They open one sport at a time. The pricing page lists which are answering
       questions today." came off on 2026-08-27 (Ned). It was a roadmap statement
       on a page about what the two models ARE, and roadmap copy has one failure
       mode: it goes stale silently. The coverage question is still answered, in
       the one place that reads the catalogue rather than restating it — Pricing's
       "Included today" line, which is generated and cannot overclaim. */
  ],
} as const;

export const KEV_MODEL = {
  eyebrow: "Kev",
  heading: "Knowledge Engine for Value",
  body: [
    "Kev prices player props and match-ups from historical data. Ask about a points line and you get the probability the model puts on it, built from the games it holds on record.",
    "It turns that probability into a break-even price. Put a chance at 40% and fair value is about $2.50, which is what the bet would have to pay to break even if the probability is right. On a multi, Kev also prices in legs that tend to land together.",
    /* ── REWRITTEN 2026-08-27 (Ned) ───────────────────────────────────────────
       The old close was "Holding the two numbers up against each other is your
       step", and every fact in front of it was a limitation: no feed, cannot
       tell you, does not name a bet. Accurate, and it added up to a shrug —
       here is a number, do what you will. That is a disclaimer wearing the
       costume of a value proposition, and it left the reader to work out on
       their own why they would want the thing.

       The constraints have NOT been softened; they are the same three sentences
       and they still come first, because they are what keeps this an analytics
       product rather than a tipping service. What is added is the point of them:
       a punter who knows what a chance is worth reads a price instead of taking
       it. That is a claim about the READER's information, which is true and
       ours to make — not a claim about outcomes, which is §1.3's line and is
       still nowhere near it. Nothing here names a bet, sizes one, or says a
       price is good. */
    "That price is Kev's own, and it is the only price Kev has. No odds feed is connected, so Kev cannot see what a bookmaker is offering on anything. It cannot tell you whether the price in front of you beats fair value, and it does not name a bet. What it gives you is your own number to bring: walk up to a market already holding an estimate of what the chance is worth, and you are reading the price rather than taking it on faith. That is the whole job. Not to tell you what to back, but to make you a more informed punter than you were before you asked.",
  ],
} as const;

export const FRANCO_MODEL = {
  eyebrow: "Franco",
  heading: "Fantasy Roster Analysis Companion",
  body: [
    "Franco works on fantasy rosters. Who to start, who to sit, who to stream, and who picks up the slack when a teammate sits out.",
    "Franco will name a starter, since a lineup puts no money on anything. He asks how your league scores first.",
    "His projections assume standard points-league scoring. Play categories and he'll tell you.",
  ],
  /* The one line kept from the Intended Use section, which was cut on
     2026-10-05 (Ned's site cut list), and its button. */
  limit: "Neither model can see your league, your budget or your bets.",
  cta: "See what's live",
} as const;
